import { get, post } from "@/api/client";
import type { LocalizedContent, TitleMap } from "@/api/content";
import type { Phase } from "@/api/journeys";

export interface ProgramItem {
  step_id: string;
  journey_id: string;
  journey_name: string;
  journey_name_i18n?: TitleMap;
  module_id: string;
  title: string;
  title_i18n?: TitleMap;
  content_type: string;
  requires_acknowledgement: boolean;
  version_no: number;
  phase: Phase;
  required: boolean;
  order_index: number;
  available_on: string | null;
  offset_days: number; // days after start; UI derives month/week/day
  content: LocalizedContent; // {lang: content}, personalisation applied
  body: string; // legacy: rendered Markdown for document/rule (primary lang)
  completed: boolean;
  acknowledged_version: number | null;
  acknowledged_at: string | null;
  needs_reack: boolean;
  has_quiz: boolean;
  quiz_passed: boolean;
}

export interface ProgramProgress {
  total: number;
  completed: number;
  required_total: number;
  required_done: number;
  percent: number;
}

export interface Program {
  user_id: string;
  full_name: string;
  start_date: string | null;
  items: ProgramItem[];
  progress: ProgramProgress;
}

async function unwrap<T>(p: Promise<{ ok: true; data: T } | { ok: false; err: { message: string } }>): Promise<T> {
  const r = await p;
  if (!r.ok) throw new Error(r.err.message);
  return r.data;
}

export const fetchMyProgram = (signal?: AbortSignal) => unwrap<Program>(get("/api/me/program", signal));
export const fetchEmployeeProgram = (userId: string, signal?: AbortSignal) =>
  unwrap<Program>(get(`/api/employees/${userId}/program`, signal));

export const completeStep = (stepId: string) => unwrap<Program>(post(`/api/me/program/steps/${stepId}/complete`));
export const incompleteStep = (stepId: string) => unwrap<Program>(post(`/api/me/program/steps/${stepId}/incomplete`));
export const acknowledgeStep = (stepId: string) => unwrap<Program>(post(`/api/me/program/steps/${stepId}/acknowledge`));
export const toggleChecklistItem = (stepId: string, itemIndex: number, checked: boolean) =>
  unwrap<Program>(post(`/api/me/program/steps/${stepId}/checklist`, { item_index: itemIndex, checked }));
